import { createContext, useContext, useEffect, useState } from 'react';
import { supabase, checkParIdentity } from './supabase.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(true);

  // Two-stage gate: (1) Supabase auth session, (2) PAR identity link, (3)
  // margin_admins membership. Re-checked via /api/check-identity whenever the
  // signed-in email changes. We track the two sub-flags separately so the
  // unauthorized screen can show the right copy ("not on PAR" vs "not yet
  // authorized for Margin").
  const [allowed, setAllowed] = useState(null);   // null = unknown, true/false = checked
  const [parIdentityOk, setParIdentityOk] = useState(null);
  const [isAdmin, setIsAdmin] = useState(null);
  const [identityError, setIdentityError] = useState(null);

  useEffect(() => {
    if (!supabase) {
      setSessionLoading(false);
      return;
    }

    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setSessionLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
    });
    return () => sub?.subscription?.unsubscribe();
  }, []);

  const email = session?.user?.email?.toLowerCase() || null;

  useEffect(() => {
    let cancelled = false;
    if (!email) {
      setAllowed(null);
      setParIdentityOk(null);
      setIsAdmin(null);
      setIdentityError(null);
      return () => { cancelled = true; };
    }
    setAllowed(null);
    setParIdentityOk(null);
    setIsAdmin(null);
    setIdentityError(null);
    checkParIdentity(email)
      .then((r) => {
        if (cancelled) return;
        setAllowed(!!r.allowed);
        setParIdentityOk(r.par_identity ?? !!r.allowed);
        setIsAdmin(!!r.is_admin);
      })
      .catch((err) => {
        if (cancelled) return;
        // Treat any failure as denied — but surface the reason so the UI
        // can distinguish "not on PAR" from "service unavailable" from
        // "not yet authorized for Margin".
        setAllowed(false);
        setParIdentityOk(false);
        setIsAdmin(false);
        setIdentityError(err);
      });
    return () => { cancelled = true; };
  }, [email]);

  const user = session?.user || null;
  // Loading means: still resolving session, OR have a session but haven't
  // finished checking PAR identity yet.
  const loading = sessionLoading || (!!session && allowed === null);

  return (
    <AuthContext.Provider value={{ session, user, email, allowed, parIdentityOk, isAdmin, loading, identityError }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const v = useContext(AuthContext);
  if (!v) throw new Error('useAuth must be used inside <AuthProvider>');
  return v;
}
